import { useCallback, useEffect, useMemo, useState } from "react";
import { useIso2700127701DebouncedValue, useIso2700127701LatestRequest } from "../../../components/shared/useIso2700127701FilteredRequest";
import { getIso2700127701DocumentosSistema } from "../api/documentos-sistema";
import type { Iso2700127701DocumentosSistemaFilters, Iso2700127701DocumentosSistemaResponse } from "../../../types/iso2700127701";

export function useIso2700127701DocumentosSistema(filters: Iso2700127701DocumentosSistemaFilters) {
  const [data, setData] = useState<Iso2700127701DocumentosSistemaResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const debouncedSearch = useIso2700127701DebouncedValue(filters.search.trim(), 300);
  const { runLatest, hasActiveRequest } = useIso2700127701LatestRequest();
  const requestFilters = useMemo<Iso2700127701DocumentosSistemaFilters>(() => ({ ...filters, search: debouncedSearch }), [debouncedSearch, filters.estado, filters.modelo_distribucion, filters.nivel, filters.tipo]);
  const requestKey = useMemo(() => JSON.stringify(requestFilters), [requestFilters]);

  const reload = useCallback(async (force = true) => {
    setLoading(true);
    setError(null);
    try {
      const response = await runLatest(requestKey, (signal) => getIso2700127701DocumentosSistema(requestFilters, signal), force);
      if (response) setData(response);
    } catch {
      setError("No se pudieron cargar los documentos del sistema.");
    } finally {
      if (!hasActiveRequest()) setLoading(false);
    }
  }, [hasActiveRequest, requestFilters, requestKey, runLatest]);

  useEffect(() => { void reload(false); }, [reload]);

  return {
    rows: data?.data.documentos ?? [], controlModificaciones: data?.data.control_modificaciones ?? [],
    filterOptions: data?.filter_options ?? { niveles: [], tipos: [], modelos_distribucion: [], estados: [] },
    meta: data?.meta ?? { total: 0 }, loading, error, reload,
  };
}
